import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, getDaysInMonth, getDate } from 'date-fns';
import { AttendanceService } from '@/lib/attendance/attendance-service';
import { useBusinessTime } from '@/hooks/ui/useBusinessTime';

export interface EmployeePlanFulfillment {
  employeeId: string;
  employeeName: string;
  // Actual hours from time tracking (clock in/out)
  hoursWorked: number;
  // Planned hours from scheduled shifts
  hoursPlanned: number;
  // Target hours from role
  hoursTarget: number;
  // Progress percentages
  actualProgressPercent: number;
  plannedProgressPercent: number;
  expectedProgressPercent: number; // Based on day of month
  // Status based on actual progress
  status: 'on_track' | 'ahead' | 'behind' | 'significantly_behind' | 'no_target';
  // Shift counts
  shiftsCompleted: number;
  shiftsScheduled: number;
  // Overtime tracking (from AttendanceService)
  overtimeMinutes: number;
  // Legacy alias for backward compatibility
  progressPercent: number;
}

export interface UsePlanFulfillmentReturn {
  fulfillmentData: Map<string, EmployeePlanFulfillment>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Query key factory for plan fulfillment
export const planFulfillmentKeys = {
  all: ['plan-fulfillment'] as const,
  business: (businessId: string) => [...planFulfillmentKeys.all, businessId] as const,
  employees: (businessId: string, employeeIds?: string[], month?: Date) =>
    [...planFulfillmentKeys.business(businessId), employeeIds?.sort().join(',') || 'all', month?.toISOString() || 'current'] as const,
};

/**
 * Fetch fulfillment data for employees
 */
async function fetchFulfillmentData(
  businessId: string,
  employeeIds?: string[],
  businessTimezone?: string,
  targetMonth?: Date
): Promise<Map<string, EmployeePlanFulfillment>> {
  const now = new Date();
  const referenceDate = targetMonth || now;
  const monthStart = startOfMonth(referenceDate);
  const monthEnd = endOfMonth(referenceDate);
  const daysInMonth = getDaysInMonth(referenceDate);

  // For current month, use actual day; for past/future months, use full month or 0
  const isCurrentMonth = startOfMonth(now).getTime() === monthStart.getTime();
  const currentDay = isCurrentMonth ? getDate(now) : (referenceDate < now ? daysInMonth : 0);

  // Calculate expected progress based on day of month (linear progression)
  const expectedProgressPercent = Math.round((currentDay / daysInMonth) * 100);

  // Fetch employees with their roles
  let employeesQuery = supabase
    .from('employees')
    .select(`
      id,
      name,
      role_id,
      roles (
        id,
        name,
        hours_per_month
      )
    `)
    .eq('business_id', businessId);

  if (employeeIds && employeeIds.length > 0) {
    employeesQuery = employeesQuery.in('id', employeeIds);
  }

  const { data: employees, error: employeesError } = await employeesQuery;

  if (employeesError) throw employeesError;

  if (!employees || employees.length === 0) {
    return new Map();
  }

  const employeeIdsList = employees.map(e => e.id);

  // Use AttendanceService as single source of truth for hours calculations (timezone-aware)
  const attendanceService = new AttendanceService(businessId, undefined, businessTimezone || 'UTC');
  const attendanceData = await attendanceService.getMonthlyAttendanceByEmployee(
    employeeIdsList,
    monthStart,
    monthEnd
  );

  // Build fulfillment data for each employee
  const resultMap = new Map<string, EmployeePlanFulfillment>();

  employees.forEach(employee => {
    const attendance = attendanceData.get(employee.id);
    const hoursWorked = attendance?.hoursWorked || 0;
    const hoursPlanned = attendance?.hoursPlanned || 0;
    const hoursTarget = employee.roles?.hours_per_month || 0;
    const shiftsCompleted = attendance?.shiftsCompleted || 0;
    const shiftsScheduled = attendance?.shiftsScheduled || 0;
    const overtimeMinutes = attendance?.overtimeMinutes || 0;

    let actualProgressPercent = 0;
    let plannedProgressPercent = 0;
    let status: EmployeePlanFulfillment['status'] = 'no_target';

    if (hoursTarget > 0) {
      actualProgressPercent = Math.round((hoursWorked / hoursTarget) * 100);
      plannedProgressPercent = Math.round((hoursPlanned / hoursTarget) * 100);

      // Determine status based on actual progress vs expected
      const progressDiff = actualProgressPercent - expectedProgressPercent;

      if (progressDiff >= 10) {
        status = 'ahead';
      } else if (progressDiff >= -10) {
        status = 'on_track';
      } else if (progressDiff >= -25) {
        status = 'behind';
      } else {
        status = 'significantly_behind';
      }
    }

    resultMap.set(employee.id, {
      employeeId: employee.id,
      employeeName: employee.name,
      hoursWorked,
      hoursPlanned,
      hoursTarget,
      actualProgressPercent,
      plannedProgressPercent,
      progressPercent: actualProgressPercent, // Legacy alias
      expectedProgressPercent,
      status,
      shiftsCompleted,
      shiftsScheduled,
      overtimeMinutes,
    });
  });

  return resultMap;
}

/**
 * Hook to calculate plan fulfillment for employees based on their role's hours_per_month target.
 * Uses AttendanceService as the single source of truth for hours calculations.
 * Now uses React Query for automatic cache invalidation when shifts change.
 */
export const usePlanFulfillment = (
  businessId: string | null,
  employeeIds?: string[],
  targetMonth?: Date
): UsePlanFulfillmentReturn => {
  const queryClient = useQueryClient();

  // Get business timezone for consistent status calculations
  const { businessTimezone } = useBusinessTime();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: businessId ? planFulfillmentKeys.employees(businessId, employeeIds, targetMonth) : ['plan-fulfillment', 'disabled'],
    queryFn: () => {
      if (!businessId) return new Map<string, EmployeePlanFulfillment>();
      return fetchFulfillmentData(businessId, employeeIds, businessTimezone || 'UTC', targetMonth);
    },
    enabled: !!businessId,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  return {
    fulfillmentData: data || new Map(),
    isLoading,
    error: error as Error | null,
    refetch: () => {
      refetch();
    },
  };
};

/**
 * Get a single employee's plan fulfillment data
 */
export const useEmployeePlanFulfillment = (
  businessId: string | null,
  employeeId: string | null,
  targetMonth?: Date
): { data: EmployeePlanFulfillment | null; isLoading: boolean; error: Error | null } => {
  const { fulfillmentData, isLoading, error } = usePlanFulfillment(
    businessId,
    employeeId ? [employeeId] : undefined,
    targetMonth
  );

  const data = employeeId ? fulfillmentData.get(employeeId) || null : null;

  return { data, isLoading, error };
};

/**
 * Hook to invalidate plan fulfillment cache.
 * Call this after shift mutations to update the fulfillment data.
 */
export const useInvalidatePlanFulfillment = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.all });
    },
    invalidateBusiness: (businessId: string) => {
      queryClient.invalidateQueries({ queryKey: planFulfillmentKeys.business(businessId) });
    },
  };
};
